




























import MatrixControl from '@/components/estimates/common/controls/MatrixControl.vue';
import ParameterHelp from '@/components/estimates/common/ParameterHelp.vue';
import {EstimateState} from '@/model/api';
import {ParameterList, ParameterValues} from '@/model/api/parameters';
import {NetworkError} from '@/model/errors/NetworkError';
import {Estimate} from '@/model/Estimate';
import {EVENT_PARAMETERS_VALIDATED, EventBus} from '@/model/events';
import {log} from '@/model/logger';
import {OfflineManager} from '@/model/offline';
import {NotSubmittedParameters} from '@/model/offline/NotSubmittedParameters';
import {left, right} from 'fp-ts/lib/Either';
import {fromPairs, intersection, isEqual} from 'lodash-es';
import {Component, Inject, Prop} from 'vue-property-decorator';
import AccessLandListControl from '../controls/AccessLandListControl.vue';
import DateControl from '../controls/DateControl.vue';
import EnumControl from '../controls/EnumControl.vue';
import FloorAreaControl from '../controls/FloorAreaControl.vue';
import MultiEnumControl from '../controls/MultiEnumControl.vue';
import MultiTextControl from '../controls/MultiTextControl.vue';
import NumberControl from '../controls/NumberControl.vue';
import NumberOrRangeControl from '../controls/NumberOrRangeControl.vue';
import ReconstructionListControl from '../controls/ReconstructionListControl.vue';
import TextControl from '../controls/TextControl.vue';
import Segment from './Segment';

@Component({
    components: {
        DateControl,
        ParameterHelp,
        EnumControl,
        NumberControl,
        NumberOrRangeControl,
        FloorAreaControl,
        MatrixControl,
        MultiEnumControl,
        TextControl,
        ReconstructionListControl,
        AccessLandListControl,
        MultiTextControl,
    },
})
export default class ParametersSegment<TEstimate extends EstimateState> extends Segment<TEstimate> {
    @Inject()
    public readonly eventBus: EventBus;

    @Inject()
    public readonly model: Estimate<TEstimate>;

    @Inject()
    public readonly offlineManager: OfflineManager;

    @Inject()
    public readonly notSubmittedParameters: NotSubmittedParameters;

    @Prop({type: String, required: true})
    public readonly estateType: string;

    @Prop({type: Object, required: true})
    public readonly parameters: ParameterList;

    protected validate: boolean = false;

    protected values: ParameterValues = {};

    private originalValues: ParameterValues = {};

    private validParameters: { [parameterName: string]: boolean } =
        fromPairs(Object.keys(this.parameters).map(name => [name, this.parameters[name].optional === true]));

    private clearCounter: number = 0;

    public created(): void {
        this.beforeEnter();
    }

    public async submit() {
        const values: ParameterValues = this.values;

        this.eventBus.emit(
            EVENT_PARAMETERS_VALIDATED,
            Object.fromEntries(Object.entries(values).filter(([name, value]) => this.validParameters[name])),
        );

        if (Object.values(this.validParameters).includes(false)) {
            this.validate = true;

            log('There are invalid parameters in segment');
            return left(null);
        }

        try {
            const estimate = await this.model.updateParameters(values);
            this.validate = false;
            this.clearNotSubmittedParameters();

            return right(estimate);
        } catch (error) {
            this.offlineManager.stagedParameters.stageSegment(values);

            if (error instanceof NetworkError) {
                return left(error);
            }

            log(error.message);
            return left(null);
        }
    }

    public hasNotSubmittedChanges(): boolean {
        const changedParameters = Object.keys(this.notSubmittedParameters.get());

        return intersection(changedParameters, Object.keys(this.values)).length !== 0;
    }

    public clearNotSubmittedChanges(): void {
        this.values = this.passedValues();
        this.clearNotSubmittedParameters();
        this.clearCounter++;
    }

    public allowsMovingOn(): boolean {
        return Object.values(this.parameters).every(p => p.value !== null || p.optional);
    }

    public beforeEnter(): void {
        this.values = Object.assign(this.passedValues(), this.notSubmittedParameters.get());
        this.originalValues = Object.assign({}, this.values);
        this.clearCounter++;
    }

    protected valueUpdated(parameterName: string, value: any): void {
        if (value === null
            || isEqual(this.comparableValue(value), this.comparableValue(this.originalValues[parameterName]))) {
            this.notSubmittedParameters.remove([parameterName]);
        } else {
            this.notSubmittedParameters.add(parameterName, value);
        }

        this.values[parameterName] = value;
    }

    protected valueValidated(name: string, valid: boolean): void {
        this.validParameters = Object.assign(
            {},
            this.validParameters,
            {[name]: valid && (this.parameters[name].optional || this.values[name] !== null)},
        );
    }

    private passedValues(): ParameterValues {
        return fromPairs(Object.entries(this.parameters).map(([name, def]) => [name, def.value]));
    }

    private clearNotSubmittedParameters(): void {
        this.notSubmittedParameters.remove(Object.keys(this.values));
    }

    /**
     * If value is object, Vue makes it reactive and it cannot be compared property by property.
     * This makes objects POJOs
     */
    private comparableValue<T>(value: T): T {
        return JSON.parse(JSON.stringify(value));
    }
}
